import React, { useState, useEffect, useRef } from 'react';
import { Plus, Sprout, Package, RefreshCw, Pause, Leaf } from 'lucide-react';
import useAppStore from '../store';
import TaskList from '../components/tasks/TaskList';
import ProductsModal from '../components/modals/ProductsModal';
import PhotoSignatureModal from '../components/modals/PhotoSignatureModal';
import CancelTaskModal from '../components/modals/CancelTaskModal';
import CompleteTaskModal from '../components/modals/CompleteTaskModal';
import DeleteTaskModal from '../components/modals/DeleteTaskModal';
import DumpsterSelectionModal from '../components/modals/DumpsterSelectionModal';
import FuelDumpsterTaskForm from '../components/FuelDumpsterTaskForm';
import PhytoTaskForm from '../components/PhytoTaskForm';
import EngraisTaskForm from '../components/EngraisTaskForm';
import EditFuelTaskModal from '../components/modals/EditFuelTaskModal';
import { Task } from '../types';
import { supabase } from '../lib/supabase';

const TasksPage: React.FC = () => {
  const { tasks, clients, drivers, users, dumpsters, depots, currentUser, addTask, completeTask, cancelTask, deleteTask, startTask, updateTask, assignDumpsterToTask, loadInitialData } = useAppStore();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPhytoModal, setShowPhytoModal] = useState(false);
  const [showEngraisModal, setShowEngraisModal] = useState(false);
  const [showFuelDumpsterModal, setShowFuelDumpsterModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [showPhotoSignatureModal, setShowPhotoSignatureModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDumpsterSelectionModal, setShowDumpsterSelectionModal] = useState(false);
  const [showEditFuelTaskModal, setShowEditFuelTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [selectedTaskProducts, setSelectedTaskProducts] = useState<any[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const editingTaskRef = useRef<Task | null>(null);

  useEffect(() => {
    editingTaskRef.current = showEditFuelTaskModal ? selectedTask : null;
  }, [showEditFuelTaskModal, selectedTask]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const task = editingTaskRef.current;
      if (task) {
        supabase.from('tasks').update({ status: 'pending' }).eq('id', task.id);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadInitialData();
    } catch (error) {
      console.error('Error refreshing data:', error);
      alert('Une erreur est survenue lors de l\'actualisation des données');
    } finally {
      setIsRefreshing(false);
    }
  };

  const verifyTaskStatus = async (task: Task): Promise<boolean> => {
    try {
      // Vérifier le statut actuel dans la base de données
      const { data, error } = await supabase
        .from('tasks')
        .select('status')
        .eq('id', task.id)
        .single();

      if (error) throw error;

      // Si le statut a changé, afficher le message
      if (data.status !== task.status) {
        alert('Merci d\'actualiser la page');
        await loadInitialData(); // Recharger les données
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error verifying task status:', error);
      alert('Une erreur est survenue lors de la vérification du statut');
      return false;
    }
  };

  const handlePhytoSubmit = async (taskData: any) => {
    if (!currentUser?.id) {
      alert('Vous devez être connecté pour créer une tâche');
      return;
    }

    try {
      await loadInitialData();
      await addTask({
        driverId: taskData.driverId,
        clientId: taskData.clientId,
        locationId: taskData.locationId,
        date: taskData.date,
        status: taskData.status || 'pending',
        type: 'phyto',
        products: taskData.products,
        comment: taskData.comment
      });
      setShowPhytoModal(false);
    } catch (error) {
      console.error('Error creating phyto task:', error);
      alert('Une erreur est survenue lors de la création de la tâche');
    }
  };

  const handleEngraisSubmit = async (taskData: any) => {
    if (!currentUser?.id) {
      alert('Vous devez être connecté pour créer une tâche');
      return;
    }

    try {
      await loadInitialData();
      await addTask({
        driverId: taskData.driverId,
        clientId: taskData.clientId,
        locationId: taskData.locationId,
        date: taskData.date,
        status: taskData.status || 'pending',
        type: 'engrais',
        products: taskData.products,
        comment: taskData.comment
      });
      setShowEngraisModal(false);
    } catch (error) {
      console.error('Error creating engrais task:', error);
      alert('Une erreur est survenue lors de la création de la tâche');
    }
  };

  const handleFuelDumpsterSubmit = async (taskData: any) => {
    if (!currentUser?.id) {
      alert('Vous devez être connecté pour créer une tâche');
      return;
    }

    try {
      await loadInitialData();
      await addTask({
        driverId: taskData.driverId,
        clientId: taskData.clientId,
        locationId: taskData.locationId,
        date: taskData.date,
        status: taskData.status || 'pending',
        type: taskData.type,
        comment: taskData.comment,
        ...(taskData.type === 'fuel'
          ? {
              fuelType: taskData.fuelType,
              plannedLiters: taskData.plannedLiters,
              price: taskData.price,
            }
          : {
              dumpsterId: taskData.dumpsterId,
              dumpsterTaskType: taskData.dumpsterTaskType,
            }),
      });
      setShowFuelDumpsterModal(false);
    } catch (error) {
      console.error('Error creating task:', error);
      alert("Une erreur est survenue lors de la création de la tâche");
    }
  };

  const handleCompleteTask = async (task: Task) => {
    const isValid = await verifyTaskStatus(task);
    if (!isValid) return;
    
    setSelectedTask(task);
    setShowCompleteModal(true);
  };

  const handleCancelTask = async (task: Task) => {
    const isValid = await verifyTaskStatus(task);
    if (!isValid) return;
    
    setSelectedTask(task);
    setShowCancelModal(true);
  };

  const handleDeleteTask = async (task: Task) => {
    setSelectedTask(task);
    setShowDeleteModal(true);
  };

  const handleStartTask = async (task: Task) => {
    const isValid = await verifyTaskStatus(task);
    if (!isValid) return;
    
    // Si c'est une tâche de benne avec la benne "inconnue", demander de sélectionner une benne réelle
    if (task.type === 'dumpster') {
      const dumpster = dumpsters.find(d => d.id === task.dumpsterId);
      if (dumpster && (dumpster.name === 'Benne Inconnue' || dumpster.number === 'UNKNOWN')) {
        setSelectedTask(task);
        setShowDumpsterSelectionModal(true);
        return;
      }
    }
    
    try {
      await startTask(task.id);
      
      // Tester l'envoi de notification manuellement après le démarrage de la tâche
      if (currentUser?.role === 'admin') {
        try {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const { data: sessionData } = await supabase.auth.getSession();
          
          if (sessionData.session) {
            // Attendre un peu pour que le statut soit mis à jour
            setTimeout(async () => {
              try {
                const response = await fetch(`${supabaseUrl}/functions/v1/send-sms`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionData.session?.access_token}`
                  },
                  body: JSON.stringify({ taskId: task.id })
                });
                
                if (!response.ok) {
                  const errorData = await response.json();
                  console.warn('Notification warning:', errorData);
                }
              } catch (notifError) {
                console.warn('Notification error:', notifError);
              }
            }, 1000);
          }
        } catch (notifError) {
          console.warn('Session error for notification:', notifError);
        }
      }
    } catch (error) {
      console.error('Error starting task:', error);
      alert('Une erreur est survenue lors du démarrage de la tâche');
    }
  };

  const handleSetTaskToWaiting = async (task: Task) => {
    const isValid = await verifyTaskStatus(task);
    if (!isValid) return;
    
    try {
      await updateTask(task.id, { status: 'waiting' });
      await loadInitialData();
    } catch (error) {
      console.error('Error setting task to waiting:', error);
      alert('Une erreur est survenue lors de la mise en attente de la tâche');
    }
  };

  const handleDumpsterSelectedAndStartTask = async (dumpsterId: string) => {
    if (!selectedTask) return;
    
    try {
      // Assigner la benne réelle à la tâche
      await assignDumpsterToTask(selectedTask.id, dumpsterId);
      
      // Démarrer la tâche
      await startTask(selectedTask.id);
      
      // Fermer le modal
      setShowDumpsterSelectionModal(false);
      setSelectedTask(null);
      
      // Recharger les données pour refléter les changements
      await loadInitialData();
      
      // Tester l'envoi de notification manuellement après le démarrage de la tâche
      if (currentUser?.role === 'admin') {
        try {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const { data: sessionData } = await supabase.auth.getSession();
          
          if (sessionData.session) {
            // Attendre un peu pour que le statut soit mis à jour
            setTimeout(async () => {
              try {
                const response = await fetch(`${supabaseUrl}/functions/v1/send-sms`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionData.session?.access_token}`
                  },
                  body: JSON.stringify({ taskId: selectedTask.id })
                });
                
                if (!response.ok) {
                  const errorData = await response.json();
                  console.warn('Notification warning:', errorData);
                }
              } catch (notifError) {
                console.warn('Notification error:', notifError);
              }
            }, 1000);
          }
        } catch (notifError) {
          console.warn('Session error for notification:', notifError);
        }
      }
    } catch (error) {
      console.error('Error assigning dumpster and starting task:', error);
      alert('Une erreur est survenue lors de l\'assignation de la benne et du démarrage de la tâche');
    }
  };

  const handleConfirmCancel = async () => {
    if (!selectedTask) return;
    try {
      await cancelTask(selectedTask.id);
      setShowCancelModal(false);
      setSelectedTask(null);
      await loadInitialData();
    } catch (error) {
      console.error('Error cancelling task:', error);
      alert('Une erreur est survenue lors de l\'annulation de la tâche');
    }
  };

  const handleConfirmDelete = async (password: string) => {
    if (!selectedTask) return;
    try {
      await deleteTask(selectedTask.id, password);
      setShowDeleteModal(false);
      setSelectedTask(null);
      await loadInitialData();
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  };

  const handleConfirmComplete = async (data: any) => {
    if (!selectedTask) return;
    try {
      await completeTask(selectedTask.id, data);
      setShowCompleteModal(false);
      setSelectedTask(null);
      await loadInitialData();
    } catch (error) {
      console.error('Error completing task:', error);
      alert('Une erreur est survenue lors de la finalisation de la tâche');
    }
  };

  const handleOpenProductsModal = async (task: Task) => {
    try {
      const tableName = task.type === 'engrais' ? 'engrais_products' : 'phyto_products';
      const { data: products, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('task_id', task.id);

      if (error) throw error;

      setSelectedTask(task);
      setSelectedTaskProducts(products || []);
      setShowProductsModal(true);
    } catch (error) {
      console.error('Error fetching products:', error);
      alert('Une erreur est survenue lors de la récupération des produits');
    }
  };

  const handleOpenPhotoSignatureModal = (task: Task) => {
    setSelectedTask(task);
    setShowPhotoSignatureModal(true);
  };

  const handleEditTask = async (task: Task) => {
    const isValid = await verifyTaskStatus(task);
    if (!isValid) return;

    if (task.type !== 'fuel') {
      alert('Seules les tâches carburant peuvent être modifiées pour le moment');
      return;
    }

    try {
      await updateTask(task.id, { status: 'editing' });
      setSelectedTask(task);
      setShowEditFuelTaskModal(true);
      await loadInitialData();
    } catch (error) {
      console.error('Error setting task to editing:', error);
      alert('Une erreur est survenue lors de la mise en édition de la tâche');
    }
  };

  const handleConfirmEditFuelTask = async (updatedData: Partial<Task>) => {
    if (!selectedTask) return;
    try {
      await updateTask(selectedTask.id, updatedData);
      setShowEditFuelTaskModal(false);
      setSelectedTask(null);
      await loadInitialData();
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Une erreur est survenue lors de la mise à jour de la tâche');
    }
  };

  const handleCancelEditFuelTask = async () => {
    if (!selectedTask) return;
    try {
      await updateTask(selectedTask.id, { status: 'pending' });
      setShowEditFuelTaskModal(false);
      setSelectedTask(null);
      await loadInitialData();
    } catch (error) {
      console.error('Error cancelling edit:', error);
      alert('Une erreur est survenue lors de l\'annulation de l\'édition');
    }
  };

  // Fonction pour tester manuellement l'envoi de SMS
  const testSendSms = async (taskId: string) => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (!sessionData.session) {
        throw new Error('Session non valide');
      }
      
      const response = await fetch(`${supabaseUrl}/functions/v1/send-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.session.access_token}`
        },
        body: JSON.stringify({ taskId })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors de l\'envoi du SMS');
      }
      
      console.log('SMS test result:', result);
      alert('Test SMS envoyé avec succès');
    } catch (error) {
      console.error('Error testing SMS:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      alert(`Erreur lors du test SMS: ${errorMessage}`);
    }
  };

  // Vérifier si l'utilisateur est admin ou logistics
  const isAdmin = currentUser?.role === 'admin';
  const isLogistics = currentUser?.role === 'logistics';

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <h2 className="text-xl font-bold">Tâches ({tasks.length})</h2>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`bg-gray-100 text-gray-800 px-4 py-2 rounded-lg flex items-center justify-center hover:bg-gray-200 ${
                isRefreshing ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Actualisation...' : 'Actualiser'}
            </button>

            <button
              onClick={() => setShowPhytoModal(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center justify-center hover:bg-green-700"
            >
              <Sprout className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Nouvelle tâche phyto</span>
              <span className="sm:hidden">Phyto</span>
            </button>

            <button
              onClick={() => setShowEngraisModal(true)}
              className="bg-teal-600 text-white px-4 py-2 rounded-lg flex items-center justify-center hover:bg-teal-700"
            >
              <Leaf className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Nouvelle tâche engrais</span>
              <span className="sm:hidden">Engrais</span>
            </button>

            <button
              onClick={() => setShowFuelDumpsterModal(true)}
              className="bg-blue-800 text-white px-4 py-2 rounded-lg flex items-center justify-center hover:bg-blue-700"
            >
              <Package className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Nouvelle tâche carburant/benne</span>
              <span className="sm:hidden">Carburant/Benne</span>
            </button>
          </div>
        </div>
      </div>

      <TaskList
        tasks={tasks}
        clients={clients}
        drivers={drivers}
        users={users}
        dumpsters={dumpsters}
        onOpenProductsModal={handleOpenProductsModal}
        onOpenPhotoSignatureModal={handleOpenPhotoSignatureModal}
        onCancelTask={handleCancelTask}
        onCompleteTask={handleCompleteTask}
        onStartTask={handleStartTask}
        onSetTaskToWaiting={handleSetTaskToWaiting}
        onDeleteTask={currentUser?.role === 'admin' ? handleDeleteTask : undefined}
        onEditTask={isLogistics || isAdmin ? handleEditTask : undefined}
        canCompleteTask={currentUser?.role !== 'driver'}
        isAdmin={isAdmin}
        isLogistics={isLogistics}
      />

      {/* Modals */}
      {showPhytoModal && (
        <PhytoTaskForm
          onSubmit={handlePhytoSubmit}
          onCancel={() => setShowPhytoModal(false)}
        />
      )}

      {showEngraisModal && (
        <EngraisTaskForm
          onSubmit={handleEngraisSubmit}
          onCancel={() => setShowEngraisModal(false)}
        />
      )}

      {showFuelDumpsterModal && (
        <FuelDumpsterTaskForm
          onSubmit={handleFuelDumpsterSubmit}
          onCancel={() => setShowFuelDumpsterModal(false)}
        />
      )}

      {showCompleteModal && selectedTask && (
        <CompleteTaskModal
          task={selectedTask}
          clients={clients}
          depots={depots}
          onConfirm={handleConfirmComplete}
          onCancel={() => {
            setShowCompleteModal(false);
            setSelectedTask(null);
          }}
        />
      )}

      {showCancelModal && selectedTask && (
        <CancelTaskModal
          task={selectedTask}
          onConfirm={handleConfirmCancel}
          onCancel={() => {
            setShowCancelModal(false);
            setSelectedTask(null);
          }}
        />
      )}

      {showDeleteModal && selectedTask && (
        <DeleteTaskModal
          task={selectedTask}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedTask(null);
          }}
        />
      )}

      {showProductsModal && selectedTask && (
        <ProductsModal
          task={selectedTask}
          products={selectedTaskProducts}
          onClose={() => {
            setShowProductsModal(false);
            setSelectedTask(null);
            setSelectedTaskProducts([]);
          }}
        />
      )}

      {showPhotoSignatureModal && selectedTask && (selectedTask.type === 'phyto' || selectedTask.type === 'engrais') && (
        <PhotoSignatureModal
          task={selectedTask}
          photos={(selectedTask.type === 'phyto' || selectedTask.type === 'engrais') ? selectedTask.photos : undefined}
          signature={(selectedTask.type === 'phyto' || selectedTask.type === 'engrais') ? selectedTask.signature : undefined}
          onClose={() => {
            setShowPhotoSignatureModal(false);
            setSelectedTask(null);
          }}
        />
      )}

      {showEditFuelTaskModal && selectedTask && selectedTask.type === 'fuel' && (
        <EditFuelTaskModal
          task={selectedTask}
          drivers={drivers}
          clients={clients}
          users={users}
          onConfirm={handleConfirmEditFuelTask}
          onCancel={handleCancelEditFuelTask}
        />
      )}

      {showDumpsterSelectionModal && selectedTask && (
        <DumpsterSelectionModal
          task={selectedTask}
          dumpsters={dumpsters}
          onConfirm={handleDumpsterSelectedAndStartTask}
          onCancel={() => {
            setShowDumpsterSelectionModal(false);
            setSelectedTask(null);
          }}
        />
      )}
    </div>
  );
};

export default TasksPage;